import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { QuoteExplain } from "./QuoteExplain";
import { PhoneCall, FileSignature, CheckCircle2, Clock, Loader2, MessageCircle } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

const quoteNumber = (id = "") => `DEV-${id.replace(/-/g, "").slice(0, 8).toUpperCase()}`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);

export const ResultView = ({ leadId, result, title }) => {
  const [done, setDone] = useState(null);

  const [paying, setPaying] = useState(false);

  const act = async (action) => {
    try {
      await axios.post(`${API}/calc/leads/${leadId}/action`, { action });
      setDone(action);
    } catch (e) {
      toast.error("Une erreur est survenue, appelez-nous au 01 48 20 98 27");
    }
  };

  const subscribe = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { lead_id: leadId, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Paiement indisponible, appelez-nous au 01 48 20 98 27");
      setPaying(false);
    }
  };

  if (result.rappel) {
    return (
      <div className="text-center py-6" data-testid="calc-result-rappel">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#102692]/10 rounded-2xl mb-4">
          <Clock className="w-8 h-8 text-[#102692]" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">{result.message}</h2>
        <p className="text-gray-500 text-sm mt-2">Votre demande a bien été transmise à nos conseillers.</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="text-center py-6" data-testid="calc-result-done">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
        <h2 className="text-xl font-bold text-gray-900">
          {done === "rappel" ? "Un conseiller vous rappelle sous 24 h" : "Demande de souscription envoyée"}
        </h2>
        <p className="text-gray-500 text-sm mt-2">Nous revenons vers vous très rapidement.</p>
      </div>
    );
  }

  return (
    <div className="text-center" data-testid="calc-result-price">
      <p className="text-sm text-gray-500 mb-1">Votre tarif annuel</p>
      <p className="text-5xl font-bold text-[#102692]" data-testid="calc-result-annual">{eur(result.annual)}</p>
      <p className="text-gray-600 mt-2" data-testid="calc-result-monthly">soit {eur(result.monthly)} / mois</p>
      {result.frais_inclus > 0 && (
        <p className="text-xs text-gray-400 mt-1" data-testid="calc-result-frais-inclus">dont {eur(result.frais_inclus)} de frais de dossier</p>
      )}
      {!result.frais_inclus && (
        <div className="mt-4 py-3 border-y border-gray-100 flex justify-between text-sm" data-testid="calc-result-frais">
          <span className="text-gray-500">Frais de dossier (une fois, à la souscription)</span>
          <span className="font-semibold text-gray-900">{eur(result.frais_dossier)}</span>
        </div>
      )}
      {result.a_payer > 0 && (
        <div className="mt-3 py-3 border-b border-gray-100 flex justify-between text-sm" data-testid="calc-result-a-payer">
          <span className="text-gray-500">À régler à la souscription ({result.mois_a_payer} mois{result.frais_dossier > 0 ? " + frais de dossier" : ""})</span>
          <span className="font-semibold text-gray-900">{eur(result.a_payer)}</span>
        </div>
      )}
      {result.a_payer > 0 && result.mois_a_payer < 12 && (
        <div className="py-3 border-b border-gray-100 flex justify-between text-sm" data-testid="calc-result-installments">
          <span className="text-gray-500">Puis prélèvement automatique par carte ({12 - result.mois_a_payer} mois)</span>
          <span className="font-semibold text-gray-900">{eur(result.monthly)} / mois</span>
        </div>
      )}
      <p className="text-sm text-gray-600 mt-3" data-testid="calc-result-mention">Tarif indicatif, confirmé par un conseiller</p>
      <a href={whatsappLink(`Bonjour, je souhaite finaliser mon devis ${(title || "").replace(/^Devis\s*/i, "")} : ${eur(result.annual)}/an (${eur(result.monthly)}/mois). N° de devis : ${quoteNumber(leadId)}`)}
        target="_blank" rel="noopener noreferrer" data-testid="calc-whatsapp-btn"
        className="mt-4 inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 px-6 rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold transition-colors">
        <MessageCircle className="w-5 h-5" /> Finaliser sur WhatsApp
      </a>
      <p className="mt-1 text-xs text-gray-400" data-testid="calc-quote-number">N° de devis : {quoteNumber(leadId)}</p>
      <QuoteExplain leadId={leadId} />
      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        <Button variant="outline" onClick={() => act("rappel")} className="h-12 rounded-xl border-[#102692] text-[#102692]" data-testid="calc-callback-btn">
          <PhoneCall className="w-4 h-4 mr-2" /> Être rappelé
        </Button>
        <Button onClick={subscribe} disabled={paying} className="h-12 rounded-xl bg-[#F97316] hover:bg-[#ea580c] text-white" data-testid="calc-subscribe-btn">
          {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSignature className="w-4 h-4 mr-2" />} Souscrire
        </Button>
      </div>
    </div>
  );
};
